import { useTranslation } from "react-i18next";
import { DownloadSimple } from "@phosphor-icons/react";
import { formatCurrency, formatDateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { SectionShell, StatusPill } from "@/components/therapist/settings/section-primitives";
import { StatCard } from "@/components/therapist/finances/FinanceRows";

interface SubscriptionInvoiceRow {
  id: string;
  receiptNumber: string | null;
  planType: "semiannual" | "annual" | "custom";
  amountGross: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  completedAt: string | null;
  createdAt: string;
}

export function InvoicesTab({
  invoices,
  isFetching,
  totalPaidToPlatform,
  platformPaid,
  platformOwes,
}: {
  invoices: SubscriptionInvoiceRow[];
  isFetching: boolean;
  totalPaidToPlatform: number;
  platformPaid: number;
  platformOwes: number;
}) {
  const { t } = useTranslation();
  return (
    <>
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label={t("therapist.finances.paidToPlatform")} value={formatCurrency(totalPaidToPlatform)} tone="primary" />
        <StatCard label={t("therapist.finances.platformPaid")} value={formatCurrency(platformPaid)} tone="paid" />
        <StatCard label={t("therapist.finances.platformOwes")} value={formatCurrency(platformOwes)} tone="pending" />
      </section>

      <SectionShell
        title={t("therapist.finances.subscriptionInvoices")}
        description={t("therapist.finances.subscriptionInvoicesDesc", { count: invoices.length })}
      >
        {invoices.length === 0 && !isFetching ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("therapist.finances.noInvoices")}</p>
        ) : (
          <div>
            {invoices.map((invoice) => (
              <InvoiceRowItem key={invoice.id} invoice={invoice} />
            ))}
          </div>
        )}
      </SectionShell>
    </>
  );
}

function InvoiceRowItem({ invoice }: { invoice: SubscriptionInvoiceRow }) {
  const { t } = useTranslation();
  const period =
    invoice.periodStart && invoice.periodEnd
      ? `${formatDateShort(invoice.periodStart)} - ${formatDateShort(invoice.periodEnd)}`
      : t("therapist.finances.periodUnavailable");
  const receipt = invoice.receiptNumber ?? invoice.id;

  return (
    <div className="flex items-center gap-4 border-b border-border/60 py-3.5 first:pt-0.5 last:border-b-0 last:pb-0.5">
      <span className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatDateShort(invoice.completedAt ?? invoice.createdAt)}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{receipt}</p>
        <p className="truncate text-xs text-muted-foreground">
          {t(`therapist.finances.planTypes.${invoice.planType}`)} · {period}
        </p>
      </div>

      <span
        className="hidden shrink-0 text-sm font-semibold tabular-nums text-foreground sm:inline"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {formatCurrency(invoice.amountGross, invoice.currency)}
      </span>

      <StatusPill tone="ready" label={t("therapist.finances.statusCompleted")} className="hidden shrink-0 md:inline-flex" />

      <Button asChild variant="outline" size="sm">
        <a href={`/api/subscription/receipts/${invoice.id}/pdf`}>
          <DownloadSimple className="size-4" />
          {t("therapist.finances.download")}
        </a>
      </Button>
    </div>
  );
}
